import classNames from 'classnames';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { RootState } from 'store';

import { Form } from 'common/component/ValidationFormAndField';
import { getLanguageDirectionClass } from 'common/i18n/i18n';
import { minLength, required } from 'common/utils/validations';
import { CISecondaryButton } from 'common/viewcomp/forms/buttons';
import { CITextField } from 'common/viewcomp/forms/fields/text';

import { BackButton } from './components/BackButton';
import { PasswordTextField } from './components/PasswordTextField';
import './loginForm.scss';

interface LoginFnComponentProps {
    onBack?: () => void;
    onSubmit: ({ username, password }: { username: string; password: string }) => void;
    header: string;
    className?: string;
    prefilledUsername?: string;
    footer?: ReactNode;
    isLoading?: boolean;
}

export const LoginForm = ({
    onBack,
    onSubmit,
    header,
    className = '',
    prefilledUsername,
    footer,
    isLoading = false,
}: LoginFnComponentProps): ReactElement => {
    const { t } = useTranslation('auth');
    const auth = useSelector((state: RootState) => state.auth);

    return (
        <div id="login_content" className={classNames(className, getLanguageDirectionClass())}>
            {onBack && <BackButton onClick={onBack} />}
            <h1 className="header" id="login_header">
                {header}
            </h1>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    username: prefilledUsername ?? null,
                    password: null,
                }}
                render={({ handleSubmit, invalid }): ReactNode => {
                    const disableButton = invalid;
                    const submitButtonClass =
                        (!auth.submitted || auth.error) && !isLoading
                            ? 'disabled-submit-button'
                            : 'enabled-submit-button';

                    return (
                        <form id="loginForm" onSubmit={handleSubmit} noValidate>
                            <CITextField
                                className="login_input_field username_field"
                                t={t}
                                id="username"
                                name="username"
                                labelI18NKey="login.username_label"
                                type="text"
                                margin="normal"
                                fullWidth
                                autoFocus
                                required
                                disabled={isLoading}
                                validations={[required(), minLength(3, 'error.username_too_short')]}
                            />
                            <PasswordTextField disabled={isLoading} />
                            <CISecondaryButton
                                className={classNames('submit_button', submitButtonClass)}
                                type="submit"
                                id="loginButton"
                                fullWidth
                                disabled={disableButton}
                                isLoading={isLoading}
                            >
                                {t('login.submitbutton')}
                            </CISecondaryButton>
                        </form>
                    );
                }}
            />
            {footer}
        </div>
    );
};
